import { FC, useMemo, useState } from 'react';

import { QRCodeSVG } from 'qrcode.react';

import { Button } from '@/components/shadcn/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogTitle } from '@/components/shadcn/ui/dialog';
import { cn } from '@/utils';

import { useMessageStack } from '../hooks';
import { CoinwebWalletMessage } from '../types';

import { CoinwebQrMessageStack } from './CoinwebQrMessageStack';

type Props = {
  title: string;
  description: string;
  message: CoinwebWalletMessage | null;
  qrJsonString: string | null;
  onClose: () => void;
  onClick?: () => void;
};

export const CoinwebQrDialog: FC<Props> = ({ message, qrJsonString, title, description, onClose, onClick }) => {
  const messages = useMessageStack(message);
  const [isQrEnlarged, setIsQrEnlarged] = useState(false);

  const content = useMemo(() => {
    if (!qrJsonString) {
      return null;
    }

    const encodedString = typeof window !== 'undefined' ? btoa(qrJsonString) : '';
    const redirectUrl = (import.meta.env.VITE_REDIRECT_URL as string) + encodedString;
    const isMobileDevice = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);

    return (
      <Dialog
        open={!!qrJsonString}
        onOpenChange={(open) => {
          if (!open) onClose();
        }}
        aria-describedby={title}
        modal
      >
        <DialogContent
          hideClose
          className={cn(
            'max-w-[100vw] min-w-auto gap-4 transition-all duration-300',
            isQrEnlarged ? 'w-full' : 'w-[95vw] max-w-sm md:w-96'
          )}
        >
          <DialogTitle className="cursor-pointer text-center" onClick={() => setIsQrEnlarged(!isQrEnlarged)}>
            {title}
          </DialogTitle>
          <DialogDescription className="text-center" hidden>
            {description}
          </DialogDescription>
          <div className="flex flex-col items-center gap-4">
            {isMobileDevice ? (
              <div className="flex w-full flex-col gap-4">
                <Button asChild className="w-full">
                  <a className="no-underline" href={redirectUrl}>
                    Open in Coinweb Wallet
                  </a>
                </Button>
                <div className="text-sm opacity-80">Or scan QR below</div>
              </div>
            ) : null}

            <QRCodeSVG
              className={cn('transition-all duration-300')}
              onClick={onClick}
              bgColor="transparent"
              fgColor="#fff"
              value={qrJsonString}
              width="100%"
              height="100%"
              imageSettings={{ src: '/chains/coinweb.svg', width: 16, height: 16, excavate: true }}
            />
          </div>

          <div className="flex w-full justify-center">
            <CoinwebQrMessageStack message={message} />
          </div>
        </DialogContent>
      </Dialog>
    );
  }, [qrJsonString, message, messages, onClose, isQrEnlarged, title, description, onClick]);

  return content;
};
